import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Bot, FileText, Loader2, RefreshCw, Send, UserRound } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/gestionale/integrations/supabase/client";
import { adminListFiles, newsCrud } from "@/lib/db";
import { useAuth } from "@/gestionale/hooks/useAuth";
import { Button } from "@/gestionale/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/gestionale/components/ui/card";
import { Label } from "@/gestionale/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/gestionale/components/ui/select";
import { Textarea } from "@/gestionale/components/ui/textarea";
import { Input } from "@/gestionale/components/ui/input";
import { format, parseISO } from "date-fns";

type GeneratedArticle = {
  titolo: string;
  estratto: string;
  articolo: string;
};

type PlayerAnalysis = {
  analisi: string;
  profilo: string;
};

type SocialContent = {
  instagram: string;
  facebook: string;
  whatsapp: string;
};

export default function IARubiana() {
  const { user } = useAuth();
  const [partitaId, setPartitaId] = useState("");
  const [generated, setGenerated] = useState<GeneratedArticle | null>(null);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [coverImage, setCoverImage] = useState("");
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [social, setSocial] = useState<SocialContent | null>(null);
  const [socialLoading, setSocialLoading] = useState(false);
  const [improveLoading, setImproveLoading] = useState(false);
  const [giocatoreId, setGiocatoreId] = useState("");
  const [playerAnalysis, setPlayerAnalysis] = useState<PlayerAnalysis | null>(null);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [playerStats, setPlayerStats] = useState({ presenze: 0, titolari: 0, minuti: 0, gol: 0, ammonizioni: 0, espulsioni: 0 });
  const [atletaId, setAtletaId] = useState("");
  const [athleteAnalysis, setAthleteAnalysis] = useState<{ analisi: string; profilo: string } | null>(null);
  const [athleteLoading, setAthleteLoading] = useState(false);
  const [athleteStats, setAthleteStats] = useState({ presenze: 0, sessioni: 0, percentuale: 0, corsi: 0 });

  const { data: partite = [] } = useQuery({
    queryKey: ["ia-rubiana-partite"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partite")
        .select("*")
        .order("data", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: statistiche = [] } = useQuery({
    queryKey: ["ia-rubiana-statistiche", partitaId],
    enabled: !!partitaId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("statistiche_partita")
        .select("*")
        .eq("partita_id", partitaId);
      if (error) throw error;
      return data || [];
    },
  });

  const { data: siteFiles = [] } = useQuery({
    queryKey: ["ia-rubiana-site-files"],
    queryFn: adminListFiles,
  });

  const { data: persone = [] } = useQuery({
    queryKey: ["ia-rubiana-persone"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("persone")
        .select("id, nome, cognome")
        .order("cognome");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: ruoliGiocatori = [] } = useQuery({
    queryKey: ["ia-rubiana-ruoli-giocatori"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ruoli")
        .select("persona_id")
        .eq("tipo_ruolo", "Giocatore");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: ruoliAtleti = [] } = useQuery({
    queryKey: ["ia-rubiana-ruoli-atleti"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ruoli")
        .select("persona_id")
        .eq("tipo_ruolo", "Atleta");
      if (error) throw error;
      return data || [];
    },
  });

  const partita = partite.find((p: any) => p.id === partitaId);
  const giocatoreIds = new Set(ruoliGiocatori.map((r: any) => r.persona_id));
  const atletaIds = new Set(ruoliAtleti.map((r: any) => r.persona_id));
  const giocatoriDisponibili = persone.filter((p: any) => giocatoreIds.has(p.id));
  const atletiDisponibili = persone.filter((p: any) => atletaIds.has(p.id));
  const giocatoreSelezionato = giocatoriDisponibili.find((p: any) => p.id === giocatoreId);
  const atletaSelezionato = atletiDisponibili.find((p: any) => p.id === atletaId);

  const pubblicaNews = async () => {
    if (!generated || !partita) {
      toast.error("Genera prima un articolo");
      return;
    }

    if (!confirm("Pubblicare questa bozza nella sezione News del sito?")) return;

    setPublishing(true);
    try {
      await newsCrud.create({
        title: generated.titolo.trim(),
        excerpt: generated.estratto.trim(),
        body: generated.articolo.trim(),
        category: "Squadra",
        image: coverImage || null,
        images: galleryImages,
      });

      toast.success("Articolo pubblicato nelle News del sito");
    } catch (error: any) {
      toast.error(error.message || "Errore nella pubblicazione");
    } finally {
      setPublishing(false);
    }
  };

  const generaSocial = async () => {
    if (!partita || !user) {
      toast.error("Seleziona una partita");
      return;
    }

    setSocialLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Sessione non valida");

      const giocatori = statistiche.map((stat: any) => {
        const persona = persone.find((p: any) => p.id === stat.persona_id);
        return {
          nome: persona ? `${persona.nome} ${persona.cognome}` : "Giocatore",
          titolare: !!stat.titolare,
          minuti: stat.minuti_giocati || 0,
          gol: stat.goal || 0,
          ammonizioni: stat.ammonizioni || 0,
          espulsioni: stat.espulsioni || 0,
        };
      });

      const response = await fetch("/api/ia-articolo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          partita,
          giocatori,
          modalita: "social",
          articolo_base: generated?.articolo || "",
        }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Errore nella generazione social");

      setSocial(result);
      toast.success("Contenuti social generati");
    } catch (error: any) {
      toast.error(error.message || "Errore nella generazione dei contenuti social");
    } finally {
      setSocialLoading(false);
    }
  };

  const miglioraArticolo = async () => {
    if (!partita || !user || !generated) {
      toast.error("Genera prima un articolo");
      return;
    }

    setImproveLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Sessione non valida");

      const response = await fetch("/api/ia-articolo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          partita,
          giocatori: [],
          modalita: "migliora",
          articolo_base: generated.articolo,
        }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Errore nel miglioramento");

      setGenerated(result);
      setSocial(null);
      toast.success("Articolo migliorato");
    } catch (error: any) {
      toast.error(error.message || "Errore nel miglioramento dell'articolo");
    } finally {
      setImproveLoading(false);
    }
  };


  const analizzaGiocatore = async () => {
    if (!giocatoreSelezionato || !user) {
      toast.error("Seleziona un giocatore");
      return;
    }

    setPlayerLoading(true);
    setPlayerAnalysis(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Sessione non valida");

      const { data: stats, error } = await supabase
        .from("statistiche_partita")
        .select("*, partita:partite(data, avversario, casa_trasferta, tipo, gol_fatti, gol_subiti)")
        .eq("persona_id", giocatoreId);
      if (error) throw error;

      const partiteGiocatore = (stats || [])
        .filter((s: any) => s.partita)
        .sort((a: any, b: any) => String(a.partita.data).localeCompare(String(b.partita.data)));
      const presenze = partiteGiocatore.filter((s: any) => Number(s.minuti_giocati || 0) > 0).length;
      const titolari = partiteGiocatore.filter((s: any) => s.titolare).length;
      const minuti = partiteGiocatore.reduce((n: number, s: any) => n + Number(s.minuti_giocati || 0), 0);
      const gol = partiteGiocatore.reduce((n: number, s: any) => n + Number(s.goal || 0), 0);
      const ammonizioni = partiteGiocatore.reduce((n: number, s: any) => n + Number(s.ammonizioni || 0), 0);
      const espulsioni = partiteGiocatore.reduce((n: number, s: any) => n + Number(s.espulsioni || 0), 0);
      const summary = { presenze, titolari, minuti, gol, ammonizioni, espulsioni };
      setPlayerStats(summary);
      const ultime = partiteGiocatore.slice(-5).map((s: any) => ({
        data: s.partita.data,
        avversario: s.partita.avversario,
        casa_trasferta: s.partita.casa_trasferta,
        minuti: Number(s.minuti_giocati || 0),
        titolare: !!s.titolare,
        gol: Number(s.goal || 0),
        ammonizioni: Number(s.ammonizioni || 0),
        espulsioni: Number(s.espulsioni || 0),
      }));

      const response = await fetch("/api/ia-articolo", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          modalita: "giocatore",
          giocatore: { nome: `${giocatoreSelezionato.nome} ${giocatoreSelezionato.cognome}`, presenze, titolari, minuti, gol, ammonizioni, espulsioni, ultime },
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Errore nell'analisi del giocatore");
      setPlayerAnalysis(result);
      toast.success("Analisi giocatore generata");
    } catch (error: any) {
      toast.error(error.message || "Errore nell'analisi del giocatore");
    } finally {
      setPlayerLoading(false);
    }
  };

  const analizzaAtleta = async () => {
    if (!atletaSelezionato || !user) {
      toast.error("Seleziona un atleta");
      return;
    }

    setAthleteLoading(true);
    setAthleteAnalysis(null);
    try {
      const { data: presenze, error } = await supabase
        .from("presenze")
        .select("presente, sessione:sessioni_allenamento(data, corso)")
        .eq("persona_id", atletaId);

      if (error) throw error;

      const sessioni = (presenze || [])
        .filter((p: any) => p.sessione)
        .sort((a: any, b: any) => String(a.sessione.data).localeCompare(String(b.sessione.data)));

      const presenti = sessioni.filter((p: any) => !!p.presente);
      const corsi = new Set(sessioni.map((p: any) => p.sessione.corso).filter(Boolean));
      const summary = {
        presenze: presenti.length,
        sessioni: sessioni.length,
        percentuale: sessioni.length ? Math.round((presenti.length / sessioni.length) * 100) : 0,
        corsi: corsi.size,
      };
      setAthleteStats(summary);

      const ultime = sessioni.slice(-8).map((p: any) => ({
        data: p.sessione.data,
        corso: p.sessione.corso,
        presente: !!p.presente,
      }));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Sessione non valida");

      const response = await fetch("/api/ia-articolo", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + session.access_token },
        body: JSON.stringify({
          modalita: "atleta",
          atleta: {
            nome: atletaSelezionato.nome + " " + atletaSelezionato.cognome,
            presenze: presenti.length,
            sessioni: sessioni.length,
            percentuale: summary.percentuale,
            corsi: Array.from(corsi),
            ultime,
          },
        }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Errore nell'analisi dell'atleta");
      setAthleteAnalysis(result);
      toast.success("Analisi atleta generata");
    } catch (error: any) {
      toast.error(error.message || "Errore nell'analisi dell'atleta");
    } finally {
      setAthleteLoading(false);
    }
  };

  const generaArticolo = async () => {
    if (!partita || !user) {
      toast.error("Seleziona una partita");
      return;
    }

    setLoading(true);
    setGenerated(null);
    setSocial(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Sessione non valida");

      const giocatori = statistiche.map((stat: any) => {
        const persona = persone.find((p: any) => p.id === stat.persona_id);
        return {
          nome: persona ? `${persona.nome} ${persona.cognome}` : "Giocatore",
          titolare: !!stat.titolare,
          minuti: stat.minuti_giocati || 0,
          gol: stat.goal || 0,
          ammonizioni: stat.ammonizioni || 0,
          espulsioni: stat.espulsioni || 0,
        };
      });

      const response = await fetch("/api/ia-articolo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ partita, giocatori, modalita: "articolo" }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Errore nella generazione");
      }

      setGenerated(result);
      toast.success("Articolo generato");
    } catch (error: any) {
      toast.error(error.message || "Errore nella generazione dell'articolo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <Bot className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">IA Rubiana Calcio</h1>
            <p className="text-muted-foreground mt-1">
              Strumenti di intelligenza artificiale per la gestione della società.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Genera articolo partita
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Partita</Label>
            <Select value={partitaId} onValueChange={setPartitaId}>
              <SelectTrigger>
                <SelectValue placeholder="Seleziona una partita..." />
              </SelectTrigger>
              <SelectContent>
                {partite.map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>
                    {format(parseISO(p.data), "dd/MM/yyyy")} — {p.casa_trasferta} vs {p.avversario} — {p.gol_fatti}-{p.gol_subiti}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {partita && (
            <div className="rounded-lg border bg-muted/20 p-4 text-sm space-y-1">
              <div><strong>{partita.casa_trasferta} vs {partita.avversario}</strong></div>
              <div className="text-muted-foreground">
                {format(parseISO(partita.data), "dd/MM/yyyy")} · {partita.tipo} · Risultato {partita.gol_fatti}-{partita.gol_subiti}
              </div>
              <div className="text-muted-foreground">
                {partita.note ? "Note presenti: sì" : "Nessuna nota inserita"}
              </div>
            </div>
          )}

          <Button onClick={generaArticolo} disabled={!partitaId || loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Luna sta scrivendo...
              </>
            ) : (
              <>
                <Bot className="mr-2 h-4 w-4" />
                Genera articolo
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            Strumenti IA
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Seleziona una partita per attivare gli strumenti. Puoi creare l'articolo, i contenuti social e migliorare una bozza già generata.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-3">
            <Button
              onClick={generaArticolo}
              disabled={!partitaId || loading || socialLoading || improveLoading}
              className="justify-start"
            >
              <FileText className="mr-2 h-4 w-4" />
              Genera articolo
            </Button>
            <Button
              variant="outline"
              onClick={generaSocial}
              disabled={!partitaId || loading || socialLoading || improveLoading}
              className="justify-start"
            >
              <Send className="mr-2 h-4 w-4" />
              Genera post social
            </Button>
            <Button
              variant="outline"
              onClick={miglioraArticolo}
              disabled={!generated || loading || socialLoading || improveLoading}
              className="justify-start"
            >
              <Bot className="mr-2 h-4 w-4" />
              Migliora articolo
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><UserRound className="h-5 w-5 text-primary" />Analizza giocatore</CardTitle>
          <p className="text-sm text-muted-foreground">Legge le statistiche reali del giocatore e crea un’analisi stagionale senza inventare dati.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Giocatore</Label>
            <Select value={giocatoreId} onValueChange={setGiocatoreId}>
              <SelectTrigger><SelectValue placeholder="Seleziona un giocatore..." /></SelectTrigger>
              <SelectContent>{giocatoriDisponibili.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.nome} {p.cognome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={analizzaGiocatore} disabled={!giocatoreId || playerLoading}>
            {playerLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analizzo il giocatore...</> : <><UserRound className="mr-2 h-4 w-4" />Genera analisi</>}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserRound className="h-5 w-5 text-primary" />
            Analizza atleta
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Analizza le presenze reali negli allenamenti di palestra. Questa statistica è separata da quella delle partite.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Atleta</Label>
            <Select value={atletaId} onValueChange={setAtletaId}>
              <SelectTrigger><SelectValue placeholder="Seleziona un atleta..." /></SelectTrigger>
              <SelectContent>
                {atletiDisponibili.map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>{p.nome} {p.cognome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={analizzaAtleta} disabled={!atletaId || athleteLoading}>
            {athleteLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analizzo l'atleta...</> : <><UserRound className="mr-2 h-4 w-4" />Genera analisi</>}
          </Button>
        </CardContent>
      </Card>

      {generated && (
        <Card>
          <CardHeader>
            <CardTitle>Bozza generata</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label>Titolo</Label>
              <Input value={generated.titolo} onChange={(e) => setGenerated({ ...generated, titolo: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Estratto</Label>
              <Textarea value={generated.estratto} onChange={(e) => setGenerated({ ...generated, estratto: e.target.value })} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Articolo</Label>
              <Textarea value={generated.articolo} onChange={(e) => setGenerated({ ...generated, articolo: e.target.value })} rows={18} />
            </div>
            <div className="space-y-4 rounded-xl border border-border bg-muted/20 p-4">
              <div>
                <Label>Foto dell'articolo</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Scegli una foto principale e, se vuoi, una o più foto per la galleria. Le immagini vengono prese dalla libreria del sito.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Foto principale</Label>
                {siteFiles.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessuna immagine disponibile nella libreria.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-h-80 overflow-y-auto pr-1">
                    {siteFiles.map((file: any) => (
                      <button
                        type="button"
                        key={file.id}
                        onClick={() => setCoverImage(file.public_url === coverImage ? "" : file.public_url)}
                        className={`relative overflow-hidden rounded-lg border-2 text-left transition-all ${
                          file.public_url === coverImage
                            ? "border-primary ring-2 ring-primary/30"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        <img src={file.public_url} alt={file.label || ""} className="aspect-square w-full object-cover" />
                        <div className="p-2 text-xs truncate">{file.label || "Immagine"}</div>
                        {file.public_url === coverImage && (
                          <span className="absolute top-2 right-2 rounded-full bg-primary px-2 py-1 text-[10px] font-bold text-primary-foreground">
                            COPERTINA
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Galleria foto (opzionale)</Label>
                {siteFiles.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessuna immagine disponibile nella libreria.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-h-80 overflow-y-auto pr-1">
                    {siteFiles.map((file: any) => {
                      const selected = galleryImages.includes(file.public_url);
                      return (
                        <button
                          type="button"
                          key={file.id}
                          onClick={() =>
                            setGalleryImages((current) =>
                              selected
                                ? current.filter((url) => url !== file.public_url)
                                : [...current, file.public_url]
                            )
                          }
                          className={`relative overflow-hidden rounded-lg border-2 text-left transition-all ${
                            selected
                              ? "border-primary ring-2 ring-primary/30"
                              : "border-border hover:border-primary/50"
                          }`}
                        >
                          <img src={file.public_url} alt={file.label || ""} className="aspect-square w-full object-cover" />
                          <div className="p-2 text-xs truncate">{file.label || "Immagine"}</div>
                          {selected && (
                            <span className="absolute top-2 right-2 rounded-full bg-primary px-2 py-1 text-[10px] font-bold text-primary-foreground">
                              ✓
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                {galleryImages.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {galleryImages.length} {galleryImages.length === 1 ? "foto selezionata" : "foto selezionate"} per la galleria.
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={generaArticolo} disabled={loading || publishing || socialLoading || improveLoading}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Rigenera
              </Button>
              <Button
                variant="outline"
                onClick={miglioraArticolo}
                disabled={loading || publishing || socialLoading || improveLoading}
              >
                {improveLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Miglioro l'articolo...
                  </>
                ) : (
                  <>
                    <Bot className="mr-2 h-4 w-4" />
                    Migliora articolo
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={generaSocial}
                disabled={loading || publishing || socialLoading || improveLoading}
              >
                {socialLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creo i social...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Genera post social
                  </>
                )}
              </Button>
              <Button
                onClick={pubblicaNews}
                disabled={loading || publishing}
                className="bg-yellow-400 text-zinc-950 hover:bg-yellow-300 font-bold"
              >
                {publishing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Pubblicazione...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Pubblica nelle News
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {playerAnalysis && giocatoreSelezionato && (
        <Card>
          <CardHeader><CardTitle>📊 Analisi di {giocatoreSelezionato.nome} {giocatoreSelezionato.cognome}</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
              {[
                ["Presenze", String(playerStats.presenze)], ["Titolare", String(playerStats.titolari)], ["Minuti", String(playerStats.minuti)], ["Gol", String(playerStats.gol)], ["Ammonizioni", String(playerStats.ammonizioni)], ["Espulsioni", String(playerStats.espulsioni)]
              ].map(([label,value]) => <div key={label} className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">{label}</div><div className="text-lg font-bold">{value}</div></div>)}
            </div>
            <div className="space-y-2"><Label>Analisi</Label><Textarea value={playerAnalysis.analisi} onChange={e=>setPlayerAnalysis({...playerAnalysis,analisi:e.target.value})} rows={12}/></div>
            <div className="space-y-2"><Label>Profilo giocatore</Label><Textarea value={playerAnalysis.profilo} onChange={e=>setPlayerAnalysis({...playerAnalysis,profilo:e.target.value})} rows={8}/></div>
            <Button variant="outline" onClick={analizzaGiocatore} disabled={playerLoading}><RefreshCw className="mr-2 h-4 w-4"/>Rigenera analisi</Button>
          </CardContent>
        </Card>
      )}

      {athleteAnalysis && atletaSelezionato && (
        <Card>
          <CardHeader>
            <CardTitle>🏋️ Analisi di {atletaSelezionato.nome} {atletaSelezionato.cognome}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                ["Presenze", String(athleteStats.presenze)],
                ["Sessioni", String(athleteStats.sessioni)],
                ["Frequenza", athleteStats.percentuale + "%"],
                ["Corsi", String(athleteStats.corsi)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">{label}</div>
                  <div className="text-lg font-bold">{value}</div>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label>Analisi</Label>
              <Textarea value={athleteAnalysis.analisi} onChange={e => setAthleteAnalysis({ ...athleteAnalysis, analisi: e.target.value })} rows={10} />
            </div>
            <div className="space-y-2">
              <Label>Profilo atleta</Label>
              <Textarea value={athleteAnalysis.profilo} onChange={e => setAthleteAnalysis({ ...athleteAnalysis, profilo: e.target.value })} rows={7} />
            </div>
            <Button variant="outline" onClick={analizzaAtleta} disabled={athleteLoading}>
              <RefreshCw className="mr-2 h-4 w-4" />Rigenera analisi
            </Button>
          </CardContent>
        </Card>
      )}

      {social && (
        <Card>
          <CardHeader>
            <CardTitle>📱 Contenuti social</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label>Instagram</Label>
              <Textarea value={social.instagram} onChange={(e) => setSocial({ ...social, instagram: e.target.value })} rows={7} />
            </div>
            <div className="space-y-2">
              <Label>Facebook</Label>
              <Textarea value={social.facebook} onChange={(e) => setSocial({ ...social, facebook: e.target.value })} rows={7} />
            </div>
            <div className="space-y-2">
              <Label>WhatsApp</Label>
              <Textarea value={social.whatsapp} onChange={(e) => setSocial({ ...social, whatsapp: e.target.value })} rows={6} />
            </div>
            <Button variant="outline" onClick={generaSocial} disabled={socialLoading || loading || publishing || improveLoading}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Rigenera contenuti social
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
